import React from "react"
import { useEffect, useRef, useState } from "react"
import SectionBackground from "../../molecules/SectionBackground/SectionBackground"
import { css, keyframes } from "@emotion/react"

/*
 * Intro estilo 2 (campo "Estilo" = Estilo 2 (Video) en el ACF de Inicio).
 *
 * A diferencia del estilo 1, acá la línea de tiempo no la pone el CSS sino el
 * propio video: la sección se colapsa cuando la reproducción llega al final.
 * Como el archivo lo sube el editor desde WordPress, su duración y su formato
 * son desconocidos, así que todo lo de abajo está pensado para que el intro
 * nunca deje la página bloqueada:
 *
 *   ended                    -> salida normal, el video terminó
 *   error                    -> el navegador no puede con el formato (p. ej.
 *                               WebM en un Safari viejo) y descarta el recurso
 *   play() rechazado         -> el navegador bloqueó el autoplay
 *   temporizador de respaldo -> el video nunca carga o se queda pegado
 *
 * El respaldo arranca en ESPERA_SIN_METADATOS y se vuelve a armar con la
 * duración real apenas el navegador la conoce, así un video largo tampoco se
 * corta antes de tiempo.
 */

const SALIDA_DURACION = 0.8

/* Cuánto esperar mientras el navegador todavía no sabe cuánto dura el video */
const ESPERA_SIN_METADATOS = 8000

/* Colchón sobre la duración real: buffering, arranque tardío, decimales */
const MARGEN_FIN = 1500

const MEDIA_MOVIL = "(max-width: 767px)"
const MEDIA_SIN_MOVIMIENTO = "(prefers-reduced-motion: reduce)"

export default function IntroVideoInicio({ videoDesktop, videoMobile }) {
  /*
   * Si el editor sube uno solo, ese sirve para ambos: es preferible mostrar el
   * video con el encuadre equivocado que dejar la pantalla en blanco.
   */
  const urlDesktop =
    videoDesktop?.mediaItemUrl || videoMobile?.mediaItemUrl || null
  const urlMobile =
    videoMobile?.mediaItemUrl || videoDesktop?.mediaItemUrl || null

  const videoDesktopRef = useRef(null)
  const videoMobileRef = useRef(null)
  const [saliendo, setSaliendo] = useState(false)

  useEffect(() => {
    if (!urlDesktop && !urlMobile) return undefined

    const bodyHtml = document.querySelector("body")
    const esMovil = window.matchMedia(MEDIA_MOVIL).matches
    const video = esMovil ? videoMobileRef.current : videoDesktopRef.current
    const videoOculto = esMovil
      ? videoDesktopRef.current
      : videoMobileRef.current

    /*
     * Los dos <video> salen en el HTML del SSR y se eligen por CSS, igual que
     * el banner: así el navegador empieza a bajar el archivo al parsear el HTML
     * y no recién al hidratar React, que es justo lo que más se nota en el
     * hero. El que no va se pausa para que no gaste CPU decodificando algo que
     * nadie ve (el archivo ya se descargó, pero son piezas livianas).
     */
    if (videoOculto) videoOculto.pause()

    const desbloquear = () => bodyHtml.classList.remove("no-scroll")

    /*
     * prefers-reduced-motion: el CSS ya esconde la sección entera, así que acá
     * solo hay que no reproducir nada ni bloquear el scroll.
     */
    if (window.matchMedia(MEDIA_SIN_MOVIMIENTO).matches) {
      if (video) video.pause()
      desbloquear()
      return undefined
    }

    if (!video) return undefined

    bodyHtml.classList.add("no-scroll")

    let temporizador = null
    let terminado = false

    /*
     * terminar() es idempotente: lo llaman el final del video, los errores y el
     * temporizador, y cualquiera de ellos puede ganar la carrera.
     */
    const terminar = () => {
      if (terminado) return
      terminado = true
      clearTimeout(temporizador)
      setSaliendo(true)
      /* El scroll se suelta recién cuando la sección terminó de colapsar */
      temporizador = setTimeout(desbloquear, SALIDA_DURACION * 1000)
    }

    const armarRespaldo = ms => {
      clearTimeout(temporizador)
      temporizador = setTimeout(terminar, ms)
    }

    /* Mientras no se sepa la duración, el respaldo es un tope fijo */
    armarRespaldo(ESPERA_SIN_METADATOS)

    const alCargarMetadatos = () => {
      if (Number.isFinite(video.duration) && video.duration > 0) {
        armarRespaldo(video.duration * 1000 + MARGEN_FIN)
      }
    }

    /*
     * El error se escucha en el <video> y no en un <source> hijo: cuando el
     * navegador agota los <source> no dispara error en el elemento, así que con
     * hijos una falla de formato pasaría en silencio y el intro se quedaría
     * pegado hasta el respaldo. Por eso el archivo va en src directo.
     */
    video.addEventListener("loadedmetadata", alCargarMetadatos)
    video.addEventListener("ended", terminar)
    video.addEventListener("error", terminar)

    /*
     * muted se fuerza por propiedad: React no lo emite como atributo en el HTML
     * del SSR, así que el primer intento de autoplay (el del atributo autoPlay,
     * antes de hidratar) sale como video con sonido y el navegador lo bloquea.
     * Con la propiedad puesta, el play() de abajo ya arranca en silencio.
     */
    video.muted = true

    /*
     * Este play() explícito además sirve para enterarse cuando el navegador lo
     * rechaza (modo de bajo consumo, políticas de ahorro de datos) y salir en
     * vez de dejar un cuadro congelado encima de la página.
     */
    const reproduccion = video.play()
    if (reproduccion && typeof reproduccion.catch === "function") {
      reproduccion.catch(terminar)
    }

    return () => {
      video.removeEventListener("loadedmetadata", alCargarMetadatos)
      video.removeEventListener("ended", terminar)
      video.removeEventListener("error", terminar)
      clearTimeout(temporizador)
      desbloquear()
    }
  }, [urlDesktop, urlMobile])

  /* Sin video cargado en el CMS no hay intro que mostrar */
  if (!urlDesktop && !urlMobile) return null

  /* ── Keyframes ── */

  /*
   * Mismo colapso que el estilo 1: la sección baja de opacidad y de alto a la
   * vez, así lo que viene abajo sube en lugar de aparecer de golpe.
   */
  const sectionAnim = keyframes`
    from {
      opacity: 1;
      height: 100vh;
    }
    to {
      opacity: 0;
      height: 0vh;
    }
  `

  /* ── Section ── */

  const sectionCss = css`
    height: 100vh;
    position: relative;
    inset: 0;
    z-index: 999;
    /* Como en el estilo 1: el intro no se come los clicks del menú */
    pointer-events: none;
    overflow: hidden;
    /*
     * Fondo neutro para el instante entre el primer pintado y el primer cuadro
     * del video. No sale del CMS a propósito: color_fondo_intro solo se edita
     * en el estilo 1, así que su valor guardado no tiene nada que ver con el
     * video que esté subido acá.
     */
    background-color: #ffffff;
    ${saliendo &&
    css`
      animation: ${sectionAnim} ${SALIDA_DURACION}s ease forwards;
    `}
    /*
     * Sin movimiento no hay intro: se descarta desde el primer pintado, así no
     * llega a verse ni un cuadro del video. El useEffect se encarga de no
     * bloquear el scroll en ese caso.
     */
    @media ${MEDIA_SIN_MOVIMIENTO} {
      display: none;
    }
  `

  const sectionContentCss = css`
    position: relative;
    height: 100%;
  `

  const videoCss = css`
    width: 100%;
    height: 100%;
    /*
     * cover recorta los márgenes del video, no su contenido: las piezas vienen
     * con harto aire alrededor del texto. contain dejaría bandas de color a los
     * costados en cuanto la pantalla no calce con el alto del archivo.
     */
    object-fit: cover;
  `

  /*
   * El desktop/móvil se resuelve acá y no con las clases only-desk / only-mov:
   * esas viven en un CSS global y Emotion inyecta sus reglas después, así que
   * el display propio del componente les ganaría y se verían los dos videos.
   */
  const videoDesktopCss = css`
    ${videoCss};
    display: block;
    @media ${MEDIA_MOVIL} {
      display: none;
    }
  `

  const videoMobileCss = css`
    ${videoCss};
    display: none;
    @media ${MEDIA_MOVIL} {
      display: block;
    }
  `

  /* ── Render ── */

  /*
   * muted + playsInline son obligatorios para que el autoplay pase en móvil:
   * los archivos traen pista de audio, así que sin muted iOS y Chrome lo
   * bloquean. Sin loop: el intro dura lo que dura el video, una sola vez.
   */
  return (
    <SectionBackground
      styleCss={sectionCss}
      styleCssContent={sectionContentCss}
      classEle="sectionintro"
    >
      {urlDesktop && (
        <video
          ref={videoDesktopRef}
          src={urlDesktop}
          css={videoDesktopCss}
          autoPlay
          muted
          playsInline
          preload="auto"
          tabIndex={-1}
          aria-hidden="true"
        />
      )}
      {urlMobile && (
        <video
          ref={videoMobileRef}
          src={urlMobile}
          css={videoMobileCss}
          autoPlay
          muted
          playsInline
          preload="auto"
          tabIndex={-1}
          aria-hidden="true"
        />
      )}
    </SectionBackground>
  )
}
